import React, { useState } from "react";
import { Modal, Button, Space, Spin, Tabs } from "antd";
import {
  DatabaseOutlined,
  ToolOutlined,
  SyncOutlined,
  ClearOutlined,
} from "@ant-design/icons";

// Imports de tipos y hooks
import {
  UnifiedStorageModalProps,
  TabKey,
  LoadingStates,
  UIState,
} from "../types/storage.types";
import { useServiceWorker } from "../hooks/useServiceWorker";
import { StorageActions } from "../services/storageActions";

// Imports de componentes de tabs
import { OverviewTab } from "../tabs/OverviewTab";
import { DiagnosticTab } from "../tabs/Diagnostic";
import Swal from "sweetalert2";
import "../css/modal.css";
import { useWidth } from "../../../../../hooks";
import { unifiedStorage } from "../../../../../helpers";

const { TabPane } = Tabs;

export const UnifiedStorageModal: React.FC<UnifiedStorageModalProps> = ({
  visible,
  onClose,
}) => {
  const width = useWidth();
  const isMobile = width === "xs";
  // Estados de UI y carga
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    main: false,
    repairing: false,
    thumbnails: {},
  });

  const [uiState, setUIState] = useState<UIState>({
    searchText: "",
    selectedRowKeys: [],
    categoryFilter: "all",
    activeTab: "overview",
    initError: null,
    issues: [],
  });

  const { swController, isReady: swReady } = useServiceWorker();

  const handleTabChange = (key: string) => {
    setUIState((prev) => ({ ...prev, activeTab: key as TabKey }));
  };

  const handleCleanupOldData = async () => {
    const result = await Swal.fire({
      title: "¿Limpiar datos?",
      html: `<p style="font-weight:bolder; color:red">Estás a punto de hacer una limpieza completa. <br/> NECESITAS TENER BUEN INTERNET <br/> ¿Estás seguro?</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Sí, limpiar",
      cancelButtonText: "Cancelar",
      customClass: {
        container: "swal-high-z-index",
      },
    });

    if (!result.isConfirmed) return;

    try {
      // ✅ PASO 1: Cerrar DB y ESPERAR
      console.log("Cerrando conexiones...");
      await unifiedStorage.close();

      // ✅ PASO 2: Delay para asegurar liberación
      await new Promise((resolve) => setTimeout(resolve, 500));

      // ✅ PASO 3: Notificar a otras pestañas que cierren
      if (typeof BroadcastChannel !== "undefined") {
        const channel = new BroadcastChannel("db-management");
        channel.postMessage({
          type: "CLOSE_DB_CONNECTIONS",
          dbName: "puce-unified-storage-mobile",
        });
        channel.close();

        // Esperar que otras pestañas respondan
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      // Mostrar loading con timer
      Swal.fire({
        title: "Limpieza completa en progreso...",
        html: "Por favor espera mientras se completa el proceso<br><span id='timer'>Tiempo: 0s</span>",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();

          let seconds = 0;
          const interval = setInterval(() => {
            seconds++;
            const timerEl = document.getElementById("timer");
            if (timerEl) {
              timerEl.innerHTML = `Tiempo: ${seconds}s`;
              if (seconds > 15) timerEl.style.color = "orange";
              if (seconds > 25) {
                timerEl.style.color = "red";
                timerEl.innerHTML = `Tiempo: ${seconds}s - Verificando bloqueos...`;
              }
            }
          }, 1000);

          (window as any).__cleanupInterval = interval;
        },
      });

      // ✅ PASO 4: Enviar mensaje al SW con timeout reducido a 30s
      const cleanupResult: any = await new Promise((resolve, reject) => {
        if (!swController) {
          reject(new Error("Service Worker no disponible"));
          return;
        }

        const channel = new MessageChannel();

        // ✅ Timeout de 30 segundos (antes era 5 minutos)
        const timeout = setTimeout(() => {
          reject(
            new Error(
              "Timeout: La limpieza completa tardó más de 30 segundos. Posible bloqueo detectado."
            )
          );
        }, 30000);

        channel.port1.onmessage = (event) => {
          clearTimeout(timeout);

          if (event.data.type === "COMPLETE_CLEANUP_RESULT") {
            resolve(event.data);
          } else if (event.data.type === "COMPLETE_CLEANUP_ERROR") {
            reject(new Error(event.data.error));
          }
        };

        swController.postMessage({ type: "CLEANUP_COMPLETED" }, [
          channel.port2,
        ]);
      });

      // Limpiar intervalo del timer
      if ((window as any).__cleanupInterval) {
        clearInterval((window as any).__cleanupInterval);
        delete (window as any).__cleanupInterval;
      }

      // Mostrar resultado
      Swal.fire({
        icon: "success",
        title: "Limpieza completa finalizada",
        html: `
        <p>Bases de datos recreadas: ${cleanupResult.totalRecreated}</p>
        <p>Elementos limpiados: ${cleanupResult.totalCleaned}</p>
        ${
          cleanupResult.totalErrors > 0
            ? `<p style="color: orange;">Errores: ${cleanupResult.totalErrors}</p>`
            : ""
        }
      `,
        confirmButtonText: "Cerrar Modal",
        confirmButtonColor: "#271080",
      }).then(() => {
        onClose();
      });
    } catch (error: any) {
      // Limpiar intervalo si hay error
      if ((window as any).__cleanupInterval) {
        clearInterval((window as any).__cleanupInterval);
        delete (window as any).__cleanupInterval;
      }

      if (
        error.message.includes("Timeout") ||
        error.message.includes("bloqueo")
      ) {
        Swal.fire({
          icon: "error",
          title: "Limpieza completa bloqueada",
          html: `
          <p>La limpieza completa se bloqueó. Posibles causas:</p>
          <ul style="text-align: left;">
            <li>Otra pestaña tiene la base de datos abierta</li>
            <li>Hay operaciones pendientes</li>
            <li>Las cachés están siendo usadas</li>
          </ul>
          <p><strong>Soluciones:</strong></p>
          <ol style="text-align: left;">
            <li><strong>Cierra TODAS las pestañas</strong> de esta aplicación</li>
            <li>Abre solo una pestaña nueva</li>
            <li>Reintenta la limpieza completa</li>
          </ol>
        `,
          confirmButtonText: "Entendido",
          customClass: {
            container: "swal-high-z-index",
          },
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error en limpieza completa",
          text: error.message || "Error desconocido durante la limpieza",
          customClass: {
            container: "swal-high-z-index",
          },
        });
      }
    }
  };

  const handleCleanupParcial = async () => {
    const result = await Swal.fire({
      title: "¿Limpiar datos?",
      html: `<p style="font-weight:bolder; color:red">Estás a punto de hacer una limpieza.<br/> ¿Estás seguro?</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Sí, limpiar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      // ✅ PASO 1: Cerrar DB y ESPERAR
      console.log("Cerrando conexiones...");
      await unifiedStorage.close();

      // ✅ PASO 2: Delay para asegurar liberación
      await new Promise((resolve) => setTimeout(resolve, 500));

      // ✅ PASO 3: Notificar a otras pestañas que cierren
      if (typeof BroadcastChannel !== "undefined") {
        const channel = new BroadcastChannel("db-management");
        channel.postMessage({
          type: "CLOSE_DB_CONNECTIONS",
          dbName: "puce-unified-storage-mobile",
        });
        channel.close();

        // Esperar que otras pestañas respondan
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      // Mostrar loading
      Swal.fire({
        title: "Limpiando datos...",
        html: "Por favor espera mientras se completa el proceso<br><span id='timer'>Tiempo: 0s</span>",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();

          let seconds = 0;
          const interval = setInterval(() => {
            seconds++;
            const timerEl = document.getElementById("timer");
            if (timerEl) {
              timerEl.innerHTML = `Tiempo: ${seconds}s`;
              if (seconds > 15) timerEl.style.color = "orange";
              if (seconds > 25) {
                timerEl.style.color = "red";
                timerEl.innerHTML = `Tiempo: ${seconds}s - Verificando bloqueos...`;
              }
            }
          }, 1000);

          // Guardar intervalo para limpiarlo después
          (window as any).__cleanupInterval = interval;
        },
      });

      // ✅ PASO 4: Enviar mensaje al SW con timeout reducido
      const cleanupResult: any = await new Promise((resolve, reject) => {
        if (!swController) {
          reject(new Error("Service Worker no disponible"));
          return;
        }

        const channel = new MessageChannel();

        // ✅ Timeout de 30 segundos (no 5 minutos)
        const timeout = setTimeout(() => {
          reject(
            new Error(
              "Timeout: La limpieza tardó más de 30 segundos. Posible bloqueo detectado."
            )
          );
        }, 30000);

        channel.port1.onmessage = (event) => {
          clearTimeout(timeout);

          if (event.data.type === "PARCIAL_CLEANUP_RESULT") {
            resolve(event.data);
          } else if (event.data.type === "PARCIAL_CLEANUP_ERROR") {
            reject(new Error(event.data.error));
          }
        };

        swController.postMessage({ type: "CLEANUP_PARCIAL" }, [channel.port2]);
      });

      // Limpiar intervalo del timer
      if ((window as any).__cleanupInterval) {
        clearInterval((window as any).__cleanupInterval);
        delete (window as any).__cleanupInterval;
      }

      // Mostrar resultado
      Swal.fire({
        icon: "success",
        title: "Limpieza completada",
        html: `
        <p>Bases de datos recreadas: ${cleanupResult.totalRecreated}</p>
        <p>Elementos limpiados: ${cleanupResult.totalCleaned}</p>
        ${
          cleanupResult.totalErrors > 0
            ? `<p style="color: orange;">Errores: ${cleanupResult.totalErrors}</p>`
            : ""
        }
      `,
        confirmButtonText: "Cerrar Modal",
        confirmButtonColor: "#271080",
      }).then(() => {
        onClose();
      });
    } catch (error: any) {
      // Limpiar intervalo si hay error
      if ((window as any).__cleanupInterval) {
        clearInterval((window as any).__cleanupInterval);
        delete (window as any).__cleanupInterval;
      }

      if (
        error.message.includes("Timeout") ||
        error.message.includes("bloqueo")
      ) {
        Swal.fire({
          icon: "error",
          title: "Operación bloqueada",
          html: `
          <p>La limpieza se bloqueó. Posibles causas:</p>
          <ul style="text-align: left;">
            <li>Otra pestaña tiene la base de datos abierta</li>
            <li>Hay datos sincronizándose</li>
          </ul>
          <p><strong>Soluciones:</strong></p>
          <ol style="text-align: left;">
            <li>Cierra otras pestañas de la app</li>
            <li>Recarga esta página</li>
            <li>Reintenta en unos segundos</li>
          </ol>
        `,
          confirmButtonText: "Entendido",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error en limpieza",
          text: error.message || "Error desconocido durante la limpieza",
        });
      }
    } finally {
      setLoadingStates({ ...loadingStates, repairing: false });
    }
  };

  const handleForceSync = () => {
    StorageActions.forceSync(swController || undefined);
  };

  return (
    <Modal
      title={
        <Space>
          <DatabaseOutlined />
          Gestor Unificado de Almacenamiento
          {!swReady && (
            <span style={{ color: "#faad14", fontSize: "12px" }}>
              (SW no disponible)
            </span>
          )}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1200}
      centered
      zIndex={1200}
      footer={
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <Button
            key="sync"
            icon={<SyncOutlined />}
            onClick={handleForceSync}
            disabled={!swController}
            style={{ flex: "1 1 auto", minWidth: "140px" }}
          >
            Sincronizar
          </Button>
          <Button
            key="repair"
            type="primary"
            icon={<ToolOutlined />}
            loading={loadingStates.repairing}
            onClick={handleCleanupParcial}
            style={{ flex: "1 1 auto", minWidth: "140px" }}
          >
            Reparar
          </Button>
          <Button
            key="cleanup"
            danger
            type="primary"
            icon={<ClearOutlined />}
            onClick={handleCleanupOldData}
            style={{ flex: "1 1 auto", minWidth: "140px" }}
          >
            Limpiar
          </Button>

          <Button
            key="close"
            onClick={onClose}
            danger
            type="dashed"
            style={{ flex: "1 1 auto", minWidth: "140px" }}
          >
            Cerrar
          </Button>
        </div>
      }
    >
      <div
        style={{
          height: `calc(100vh - ${isMobile ? "280px" : "180px"})`,
          minHeight: `calc(100vh - ${isMobile ? "280px" : "180px"})`,
          maxHeight: `calc(100vh - ${isMobile ? "280px" : "180px"})`,
          overflow: "auto",
        }}
      >
        <Spin spinning={loadingStates.main}>
          <Tabs activeKey={uiState.activeTab} onChange={handleTabChange}>
            {/* Tab Overview */}
            <TabPane tab="Resumen" key="overview">
              <OverviewTab />
            </TabPane>

            <TabPane tab="Diagnóstico" key="diagnostic">
              <DiagnosticTab />
            </TabPane>
          </Tabs>
        </Spin>
      </div>
    </Modal>
  );
};

export default UnifiedStorageModal;
